import React from "react";
import DateInput from "../components/DateInput";
import { createPortal } from "react-dom";
import { useEffect, useMemo, useState } from "react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { formatDateValue, formatReportValue } from "../lib/dateFormat";
import { normalizeFilterSearch } from "../lib/filterSearch";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  W,
  DS,
  createCeoFilters,
  setCeoPlantAliases,
} from "./CEOCommandCenter";

// MS: every KPI card of the CEO Command Center tabs as a selectable report
// column. Each value is fetched exactly the way its CEO card fetches it, so the
// report always matches the cards for the same filters.
const MODULES = [
  ["Dashboard", [W.totalSalesUsd, W.orderedMcs, W.availableMcs, W.availableKgs, W.purchaseWeight, W.receivedQty, W.shipmentOutMcs, W.shipmentOutKgs]],
  ["Sales", [W.totalSalesUsd, W.salesMcs, W.salesInvoiceCount, W.salesConsigneeCount]],
  ["Purchase", [W.purchaseWeight, W.purchaseProductionWeight, W.purchaseLotCount]],
  ["QA", [W.qcTotalTests, W.qcClearTests, W.qcDetectedTests]],
  ["Process", [
    W.receivedQty, W.freezingProductionWeight, W.dailyActualMcs,
    W.deheadingReceivedWeight, W.deheadingOutputWeight, W.looseShellWeight,
    W.vaInputWeight, W.vaOutputWeight, W.vaTransferWeight,
    W.soakInQty, W.soakOutQty, W.freezingRejectionWeight, W.dailyActualKgs, W.dummyMcs, W.masterMcs,
  ]],
  ["Store", [W.availableMcs, W.availableKgs, W.dummyStockMcs, W.dummyStockKgs, W.poPendingMcsStore]],
  ["Dispatch", [W.shipmentOutMcs, W.shipmentOutKgs]],
];

// One entry per KPI under its home tab; a KPI shown on several tabs is one column.
// Dashboard only repeats other tabs' KPIs, so it goes last and keeps just leftovers.
const KPIS = [];
[...MODULES.slice(1), MODULES[0]].forEach(([module, widgets]) => widgets.forEach((w) => {
  if (w && !KPIS.some((k) => k.id === w.id)) KPIS.push({ ...w, module });
}));
const DEFAULT_KPIS = MODULES[0][1].filter(Boolean).map((w) => w.id);
const KPI_GROUPS = [...new Set(KPIS.map((k) => k.module))]
  .map((m) => [m, KPIS.filter((k) => k.module === m).map((k) => ({ id: k.id, label: k.title }))]);

const CHIP_LIMIT = 40;
const EMPTY_COLUMN_IDS = [];

// Column chooser: selected columns as removable chips, all options in a
// searchable grouped checklist that opens on demand. Ticks inside the checklist
// are a draft, applied in one go when it closes (Done), so nothing reloads per tick.
function ColumnPicker({ title, hint, groups, selected, onChange, requiredIds = EMPTY_COLUMN_IDS, moduleOptions = [], moduleValue, onModuleChange, children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(selected);
  useEffect(() => {
    if (open) setDraft([...new Set([...requiredIds, ...selected])]);
  }, [open, requiredIds, selected]);
  const withRequired = (ids) => [...new Set([...requiredIds, ...(ids || [])])];
  const showPicker = (v) => {
    if (v) setDraft(withRequired(selected));
    else { onChange(withRequired(draft)); setQ(""); }
    setOpen(v);
  };
  const applied = new Set(selected);
  const on = new Set(draft);
  const needle = q.trim().toLowerCase();
  // A search matching a group name (e.g. "sales") shows that whole group.
  const shown = groups
    .map(([group, items, note]) => [group, needle && group.toLowerCase().includes(needle)
      ? items : items.filter((i) => !needle || i.label.toLowerCase().includes(needle)), note])
    .filter(([, items]) => items.length);
  const shownIds = shown.flatMap(([, items]) => items.map((i) => i.id));
  const total = groups.reduce((n, [, items]) => n + items.length, 0);
  const chosen = groups.flatMap(([, items]) => items).filter((i) => applied.has(i.id));
  const setMany = (ids, value) => {
    const drop = new Set(ids);
    setDraft((d) => (value
      ? withRequired([...d, ...ids])
      : withRequired(d.filter((id) => !drop.has(id) || requiredIds.includes(id)))));
  };
  const toggle = (id) => setMany([id], !on.has(id));
  const remove = (id) => {
    if (!requiredIds.includes(id)) onChange(withRequired(selected.filter((x) => x !== id)));
  };
  const linkBtn = "rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";

  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold">{title}</div>
          <div className="mt-0.5 text-xs text-slate-500">{chosen.length} of {total} selected{hint ? ` · ${hint}` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          {moduleOptions.length ? (
            <select aria-label="Filter report by module" value={moduleValue || "all"} onChange={(e) => onModuleChange?.(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
              <option value="all">All</option>
              {moduleOptions.map((module) => <option key={module} value={module}>{module}</option>)}
            </select>
          ) : null}
          {chosen.length ? <button type="button" onClick={() => onChange([...requiredIds])} className="text-xs font-medium text-slate-500 hover:text-slate-800">Clear optional</button> : null}
          <Popover open={open} onOpenChange={showPicker}>
            <PopoverTrigger asChild>
              <button type="button" className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                Choose columns ▾
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="top"
              sideOffset={8}
              avoidCollisions
              align="end"
              className="!z-[2147483647] flex max-h-[min(55vh,340px)] w-[min(720px,calc(100vw-2rem))] flex-col overflow-hidden bg-white p-0"
            >
              <div className="flex shrink-0 items-center gap-2 border-b p-3">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search columns…" className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
                <button type="button" disabled={!shownIds.length} onClick={() => setMany(shownIds, true)} className={linkBtn}>{needle ? `Select ${shownIds.length} shown` : "Select all"}</button>
                <button type="button" disabled={!shownIds.length} onClick={() => setMany(shownIds, false)} className={linkBtn}>{needle ? "Unselect shown" : "Clear"}</button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {shown.length ? shown.map(([group, items, note]) => {
                  const ids = items.map((i) => i.id);
                  const count = ids.filter((id) => on.has(id)).length;
                  return (
                    <div key={group} className="mb-3 last:mb-0">
                      {groups.length > 1 ? (
                        <div className="mb-1 flex items-center justify-between gap-3 border-b border-slate-100 pb-1">
                          <span className="min-w-0 truncate text-xs font-semibold uppercase text-slate-500" title={note || ""}>
                            {group} <span className="font-normal normal-case text-slate-400">· {count}/{ids.length}{note ? ` · ${note}` : ""}</span>
                          </span>
                          <button type="button" onClick={() => setMany(ids, count < ids.length)} className="shrink-0 text-xs font-medium text-blue-600 hover:underline">
                            {count < ids.length ? "Select all" : "Clear"}
                          </button>
                        </div>
                      ) : null}
                      <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((i) => (
                          <label key={i.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[13px] text-slate-700 hover:bg-slate-50">
                            <input type="checkbox" checked={on.has(i.id)} disabled={requiredIds.includes(i.id)} onChange={() => toggle(i.id)} />
                            <span className="truncate" title={i.label}>{i.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                }) : <div className="py-6 text-center text-sm text-slate-500">No column matches “{q}”.</div>}
              </div>
              <div className="flex shrink-0 items-center justify-between border-t px-3 py-2">
                <span className="text-xs text-slate-500">{draft.length} of {total} selected</span>
                <button type="button" onClick={() => showPicker(false)} className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">Done</button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {chosen.length ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {chosen.slice(0, CHIP_LIMIT).map((i) => (
            <span key={i.id} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 py-0.5 pl-2.5 pr-1 text-xs text-blue-900">
              {i.chip || i.label}
              {!requiredIds.includes(i.id) ? <button type="button" aria-label={`Remove ${i.chip || i.label}`} onClick={() => remove(i.id)} className="rounded-full px-1 text-blue-500 hover:bg-blue-100 hover:text-blue-900">×</button> : <span className="px-1 text-blue-500">required</span>}
            </span>
          ))}
          {chosen.length > CHIP_LIMIT ? (
            <button type="button" onClick={() => showPicker(true)} className="text-xs text-slate-500 hover:underline">+{chosen.length - CHIP_LIMIT} more</button>
          ) : null}
        </div>
      ) : (
        <div className="mt-3 text-xs text-slate-500">Nothing selected. Use “Choose columns”.</div>
      )}
      {children}
    </div>
  );
}


function MsVarietyFilter({ value, options, onChange, filterKey, filterLabel }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const [position, setPosition] = useState(null);

  const normalized = (options || []).map((o) => ({
    value: optValue(o),
    label: String(optLabel(o) ?? ""),
  }));

  const selected = normalized.find(
    (o) => String(o.value ?? "") === String(value ?? "")
  );
  const pluralFilterNames = {
    Plant: "plants",
    Buyer: "buyers",
    Consignee: "consignees",
    Country: "countries",
    Grade: "grades",
    Variety: "varieties",
    "Product Type": "product types",
    Treatment: "treatments",
    Brand: "brands",
    Species: "species",
    "Freezing Type": "freezing types",
    "Cold Store": "cold stores",
  };
  const allLabel = `All ${pluralFilterNames[filterLabel] || "options"}`;

  const searchNeedle = normalizeFilterSearch(search.trim());
  const filtered = normalized.filter((o) =>
    normalizeFilterSearch(o.label).includes(searchNeedle)
  );

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({
        top: rect.bottom + 3,
        left: rect.left,
        width: rect.width,
      });
    };

    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current?.contains(event.target) ||
        event.target.closest?.(".variety-combobox-options")
      ) return;
      setOpen(false);
      setSearch("");
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open]);

  return (
    <div className="variety-combobox" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className="variety-combobox-input rounded-xl border border-slate-300 px-3 py-2"
        role="combobox"
        aria-label="Variety"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        placeholder={allLabel}
        value={open ? search : (selected?.label ?? "")}
        onClick={() => {
          if (!open) {
            setSearch("");
            setOpen(true);
          }
        }}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            setSearch("");
          } else if (e.key === "Enter" && filtered.length > 0) {
            e.preventDefault();
            onChange(filtered[0].value);
            setSearch("");
            setOpen(false);
          }
        }}
      />
      {open && position && createPortal(
        <div
          className={`variety-combobox-options ${filterKey === "consignee" ? "ms-consignee-dropdown" : ""}`}
          role="listbox"
          style={{
            position: "fixed",
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 2147483647,
          }}
        >
          <button
            type="button"
            className="variety-combobox-option"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange("");
              setSearch("");
              setOpen(false);
            }}
          >
            {allLabel}
          </button>
          {filtered.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={String(option.value) === String(value)}
              className="variety-combobox-option"
              key={option.value}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(option.value);
                setSearch("");
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="variety-combobox-empty">
              No matching {pluralFilterNames[filterLabel] || "options"} found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

const FILTERS = [
  ["plant", "Plant", "plants"],
  ["buyer", "Buyer", "buyers"],
  ["consignee", "Consignee", "consignees"],
  ["country", "Country", "countries"],
  ["grade", "Grade", "grades"],
  ["variety", "Variety", "varieties"],
  ["productType", "Product Type", "productTypes"],
  ["treatment", "Treatment", "treatments"],
  ["brand", "Brand", "brands"],
  ["species", "Species", "species"],
  ["freezingType", "Freezing Type", "freezingTypes"],
  ["coldStore", "Cold Store", "coldStores"],
];

function defaultFilters() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { dateFrom: `${y}-04-01`, dateTo: `${y + 1}-03-31`, ...Object.fromEntries(FILTERS.map(([k]) => [k, ""])) };
}

const optValue = (o) => (typeof o === "object" && o !== null ? o.value : o);
const optLabel = (o) => (typeof o === "object" && o !== null ? o.label ?? o.value : o);

async function fetchKpi(w, filters) {
  if (w.type === "ceo-parity-kpi") {
    const r = await api.post("/ai/ceo-chat/erp-parity-kpi-v2", {
      metric: w.metric,
      from_date: filters.dateFrom || "",
      to_date: filters.dateTo || "",
      plant: filters.plant || "",
      buyer: filters.buyer || "",
      consignee: filters.consignee || "",
      country: filters.country || "",
      grade: filters.grade || "",
      variety: filters.variety || "",
      product_type: filters.productType || "",
      treatment: filters.treatment || "",
      brand: filters.brand || "",
      species: filters.species || "",
      freezing_type: filters.freezingType || "",
      cold_store: filters.coldStore || "",
    });
    return Number(r.data?.value || 0);
  }
  const r = await api.post("/widget/data", { widget: w, filters: createCeoFilters(w, filters) }, { timeout: 60000 });
  return Number(r.data?.value || 0);
}

const fmt = (w, v) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: w.number_format?.decimals ?? 2 });

// Detailed report: the detail tables the CEO Command Center tabs show, as the
// very same widget objects. Rows come from the query the CEO table runs (its
// dataset, its built-in filters such as PO-linked only, and createCeoFilters),
// and the columns offered are the ones that table shows.
const TABLE_MODULES = [
  ["Sales", [{
    ...W.salesTable,
    id: "ms-report-sales-table",
    title: "Sales — PO-Linked Cross-Module Details",
    dataset_id: DS.msSalesPoTrace,
    selected_columns: [
      ...W.salesTable.selected_columns,
      "PoID", "PoStatus", "OrderedMCS", "RevisedMCS", "DispatchedMCS", "Price",
      "CollectionRate", "DueDate", "ETAPOD", "BLDate", "BankCharges",
      "UtilizedValue", "FirstCollectionDate", "LastCollectionDate",
      "LastUtilizedDate", "SpotRate",
      "LinkedStockID", "LinkedPoGradeID", "LinkedStockGrade", "LinkedStockVarietyID",
      "LinkedStockInvoiceNumbers", "LinkedStockActualMCS", "LinkedStockAvailableMCS",
      "LinkedReceivingID", "LinkedTrackCode", "LinkedArrivalID", "LinkedArrivalBreakupID",
      "LinkedProductionID", "LinkedProductionDate", "LinkedProductionPlantID", "LinkedReceivingQty",
      "LinkedPurchaseLotID", "LinkedPurchaseArrivalNumber", "LinkedPurchaseLotNumber",
      "LinkedPurchaseDate", "LinkedSupplierName", "LinkedPurchaseStation", "LinkedPurchaseWeight",
      "LinkedStockOutID", "LinkedStockOutBatchID", "LinkedDispatchDate", "LinkedDispatchType",
      "LinkedDispatchMCS", "LinkedDispatchLooseBags",
    ],
  }]],
  ["Purchase", [{
    ...W.purchaseTable,
    id: "ms-report-purchase-table",
    title: "Purchase — Arrival-Linked Processing Details",
    dataset_id: DS.msPurchasePreprocessing,
    selected_columns: [
      "ArrivalNumber", "ArrivalDate", "LotNumber", "PurchaseDate",
      "SupplierName", "PurchaseStation", "PurchaseWeight",
      "RMLotId", "ArrivalID", "ArrivalBreakupID", "PurchaseLotID",
      "ReceivingID", "TrackCode", "ReceivingDate", "ReceivingPlantName",
      "ReceivingQty",
    ],
  }]],
  ["QA", [W.qcTable]],
  ["Pre-processing", [{
    ...W.productionTable,
    id: "ms-report-preprocessing-table",
    title: "Pre-processing Details",
    dataset_id: DS.msPreprocessing,
    selected_columns: [
      "ReceivingID", "PlantID", "PlantName", "VehicleNumber", "TrackCode",
      "ArrivalDate", "HeadOnQty", "ReceivingCount", "HLWeight", "GradingYield",
      "VaInQty", "VaOutQty", "ProcessingDate", "VaGradingQty", "VaBalQty",
      "VaYield", "SoakInQty", "SoakOutQty", "SoakingDate", "SoakinQtyS1",
      "SoakinQtyS2", "SoakBalQty", "SoakingYield", "FreezingQty",
      "RejectionWeight", "TypeProduct", "ProductionDate", "FreezingHLQty",
      "FreezingVAQty", "FreezingBalQty", "FreezingYield",
    ],
  }]],
  ["Process", [W.deheadingTable, W.vaTable, W.soakingTable, W.freezingTable, W.dailyProductionTable, W.packingTable]
    .map((widget) => ({
      ...widget,
      selected_columns: (widget.selected_columns || []).filter((column) => column !== "PoID"),
    }))],
  ["Store", [{
    ...W.stockTable,
    id: "ms-report-store-po-summary",
    title: "Store PO Summary",
    dataset_id: DS.msStore,
    selected_columns: [
      "PlantName", "STATUS", "OrderedKgs",
      "PackedKgs", "PackedMcs", "BalanceKgs", "BalanceMcs",
    ],
  }]],
  ["Dispatch", [W.shipmentTable]],
];
const SOURCES = TABLE_MODULES.flatMap(([module, widgets]) => widgets.filter(Boolean)
  .map((w) => ({ module, id: w.id, title: w.title, widget: w, columns: w.selected_columns || [] })));
const pickId = (module, column) => `${module}|${column}`;
const SALES_NATIVE_COLUMNS = new Set([
  ...W.salesTable.selected_columns,
  "PoID", "PoStatus", "OrderedMCS", "RevisedMCS", "DispatchedMCS", "Price",
  "CollectionRate", "DueDate", "ETAPOD", "BLDate", "BankCharges",
  "UtilizedValue", "FirstCollectionDate", "LastCollectionDate",
  "LastUtilizedDate", "SpotRate",
]);
const PURCHASE_NATIVE_COLUMNS = new Set([
  "ArrivalNumber", "ArrivalDate", "LotNumber", "PurchaseDate",
  "SupplierName", "PurchaseStation", "PurchaseWeight",
]);
const DEFAULT_DETAIL_COLUMN_IDS_BY_MODULE = {
  Sales: SOURCES
    .filter((source) => source.module === "Sales")
    .flatMap((source) => source.columns
      .filter((column) => SALES_NATIVE_COLUMNS.has(column))
      .map((column) => pickId(source.module, column))),
  Purchase: SOURCES
    .filter((source) => source.module === "Purchase")
    .flatMap((source) => source.columns
      .filter((column) => PURCHASE_NATIVE_COLUMNS.has(column))
      .map((column) => pickId(source.module, column))),
  Process: SOURCES
    .filter((source) => source.module === "Process" || source.module === "Pre-processing")
    .flatMap((source) => source.columns.map((column) => pickId(source.module, column))),
  Store: SOURCES
    .filter((source) => source.module === "Store")
    .flatMap((source) => source.columns.map((column) => pickId(source.module, column))),
};

const COLUMN_LABEL_OVERRIDES = {
  PoStatus: "PO Status",
  PoID: "PO ID",
  ETAPOD: "ETA POD",
  BLDate: "BL Date",
  OrderedMCS: "Ordered MCS",
  RevisedMCS: "Revised MCS",
  DispatchedMCS: "Dispatched MCS",
  ArrivalNumber: "Arrival Number",
  ArrivalDate: "Arrival Date",
  RmArrivalRate: "RM Arrival Rate",
  PurchaseArrival: "Purchase Arrival",
  InvoiceNumber: "Invoice No.",
  PoNumber: "PO Number",
  PoDate: "PO Date",
  Variety: "Variety description",
  MCS: "MCS",
  LineGrossKGS: "Line Gross KGS",
  InvoiceGrossKGS: "Invoice Gross KGS",
  PriceNative: "Rate",
  LineValueUSD: "Line Value USD",
  DestinationCountry: "Destination Country",
  ContainerNo: "Container Number",
  ShippingBillNo: "Shipment Billing Number",
  ShippingBillDate: "Shipment Billing Date",
  LotNo: "Arrival Number",
  LinkedStockID: "Linked Stock ID",
  LinkedPoGradeID: "Linked PO Grade ID",
  LinkedStockGrade: "Stock Grade",
  LinkedStockVarietyID: "Stock Variety ID",
  LinkedStockInvoiceNumbers: "Stock Invoice Numbers",
  LinkedStockActualMCS: "Stock Actual MCS",
  LinkedStockAvailableMCS: "Stock Available MCS",
  LinkedReceivingID: "Linked Receiving ID",
  LinkedTrackCode: "Receiving Track Code",
  LinkedArrivalID: "Purchase Arrival ID",
  LinkedArrivalBreakupID: "Arrival Breakup ID",
  LinkedProductionID: "Linked Production ID",
  LinkedProductionDate: "Production Date",
  LinkedProductionPlantID: "Production Plant ID",
  LinkedReceivingQty: "Receiving Quantity",
  LinkedPurchaseLotID: "Purchase Lot ID",
  RMLotId: "RMLotId (QA ↔ Purchase Link)",
  LinkedPurchaseArrivalNumber: "Purchase Arrival Number",
  LinkedPurchaseLotNumber: "Purchase Lot Number",
  LinkedPurchaseDate: "Purchase Date",
  LinkedSupplierName: "Purchase Supplier",
  LinkedPurchaseStation: "Purchase Station",
  LinkedPurchaseWeight: "Purchase Weight",
  LinkedStockOutID: "Dispatch Line ID",
  LinkedStockOutBatchID: "Dispatch Batch ID",
  LinkedDispatchDate: "Dispatch Date",
  LinkedDispatchType: "Dispatch Type",
  LinkedDispatchMCS: "Dispatch MCS",
  LinkedDispatchLooseBags: "Dispatch Loose Bags",
};
const columnLabel = (c) => COLUMN_LABEL_OVERRIDES[c] || String(c)
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
  .replace(/_/g, " ")
  .replace(/\bUsd\b/g, "USD").replace(/\bKgs\b/g, "KGS").replace(/\bMcs\b/g, "MCS");
const isIdColumn = (c) => /id$/i.test(String(c));

// Each module lists its CEO tables' columns once, in CEO order.
const COLUMN_GROUPS = TABLE_MODULES.map(([module]) => {
  const list = SOURCES.filter((s) => s.module === module);
  const columns = list.flatMap((s) => s.columns);
  const nativeColumns = {
    Sales: SALES_NATIVE_COLUMNS,
    Purchase: PURCHASE_NATIVE_COLUMNS,
  }[module];
  const items = [...new Set(nativeColumns
    ? columns.filter((column) => nativeColumns.has(column))
    : columns)].map((c) => {
    const label = `${columnLabel(c)}${isIdColumn(c) ? " (ID)" : ""}`;
    return { id: pickId(module, c), label, chip: `${module}: ${label}` };
  });
  return [module, items, list.map((s) => s.title).join(", ")];
}).filter(([, items]) => items.length);
const MS_DETAIL_COLUMNS_STORAGE_KEY = "ms-report.detailed-columns.v1";
const VALID_MS_DETAIL_COLUMN_IDS = new Set(COLUMN_GROUPS.flatMap(([, items]) => items.map((item) => item.id)));
const SALES_LINKED_COLUMN_MAP = {
  Purchase: {
    RMLotId: "LinkedPurchaseRMLotId", PurchaseLotID: "LinkedPurchaseLotID",
    ArrivalID: "LinkedPurchaseArrivalID", ArrivalBreakupID: "LinkedPurchaseArrivalBreakupID",
    ArrivalNumber: "LinkedPurchaseArrivalNumber", ArrivalDate: "LinkedPurchaseArrivalDate",
    LotNumber: "LinkedPurchaseLotNumber", PurchaseDate: "LinkedPurchaseDate",
    SupplierName: "LinkedSupplierName", PurchaseStation: "LinkedPurchaseStation",
    ReceivingID: "LinkedReceivingID", TrackCode: "LinkedTrackCode",
    ReceivingDate: "LinkedPurchaseReceivingDate", ReceivingPlantName: "LinkedPurchaseReceivingPlantName",
    PurchaseWeight: "LinkedPurchaseWeight",
    ReceivingQty: "LinkedReceivingQty",
  },
  QA: {
    QcRecordID: "LinkedQARowID", LotID: "LinkedQALotID", RMLotId: "LinkedQARMLotId",
    SampleKey: "LinkedQASampleKey", SampleDetails: "LinkedQASampleDetails",
    AnalysisDate: "LinkedQAAnalysisDate", ReportDate: "LinkedQAReportDate",
    LotNo: "LinkedQALotNo", Centre: "LinkedQACentre", PlantID: "LinkedQAPlantID",
    Plant: "LinkedQAPlant", Result: "LinkedQAResult", Parameter: "LinkedQAParameter",
    Sulphite: "LinkedQASulphite", AOZ: "LinkedQAAOZ", SEM: "LinkedQASEM",
    CAP: "LinkedQACAP", AHD: "LinkedQAAHD", AMOZ: "LinkedQAAMOZ",
    Remarks: "LinkedQARemarks", ExternalLab: "LinkedQAExternalLab",
    ExternalResult: "LinkedQAExternalResult", AbStatus: "LinkedQAAbStatus",
    TestedBy: "LinkedQATestedBy", AntibioticDetected: "LinkedQAAntibioticDetected",
    TestClear: "LinkedQATestClear",
  },
  Process: {
    ReceivingID: "LinkedReceivingID", TrackCode: "LinkedTrackCode",
    ProductionDate: "LinkedProductionDate", ProductionID: "LinkedProductionID",
    ProductionPlantID: "LinkedProductionPlantID", ReceivingQty: "LinkedReceivingQty",
    HeadOnQty: "LinkedReceivingQty",
  },
  Store: {
    StockID: "LinkedStockID", PoID: "PrimaryPoID", Grade: "LinkedStockGrade",
    ActualMCS: "LinkedStockActualMCS", AvailableMCS: "LinkedStockAvailableMCS",
  },
  Dispatch: {
    ShipmentLineID: "LinkedStockOutID", StockOutID: "LinkedStockOutID",
    ShipmentBatchID: "LinkedStockOutBatchID", StockOutBatchID: "LinkedStockOutBatchID",
    ShipmentDate: "LinkedDispatchDate", OutDate: "LinkedDispatchDate", OutType: "LinkedDispatchType",
    OutMCS: "LinkedDispatchMCS", OutLooseBags: "LinkedDispatchLooseBags",
  },
};
const SALES_LINKED_LABELS = {
  PrimaryPoID: "PO · PO ID",
  PrimaryPoNumber: "PO · PO Number",
  PrimaryPoDate: "PO · PO Date",
  LinkedPurchaseRMLotId: "Purchase · RMLotId",
  LinkedPurchaseLotID: "Purchase · Purchase Lot ID",
  LinkedPurchaseArrivalID: "Purchase · Arrival ID",
  LinkedPurchaseArrivalBreakupID: "Purchase · Arrival Breakup ID",
  LinkedPurchaseArrivalNumber: "Purchase · Arrival Number",
  LinkedPurchaseArrivalDate: "Purchase · Arrival Date",
  LinkedPurchaseLotNumber: "Purchase · Lot Number",
  LinkedPurchaseDate: "Purchase · Purchase Date",
  LinkedSupplierName: "Purchase · Supplier",
  LinkedPurchaseStation: "Purchase · Station",
  LinkedReceivingID: "Purchase · Receiving ID",
  LinkedTrackCode: "Purchase · Track Code",
  LinkedPurchaseReceivingDate: "Purchase · Receiving Date",
  LinkedPurchaseReceivingPlantName: "Purchase · Receiving Plant",
  LinkedPurchaseWeight: "Purchase · Purchase Weight",
  LinkedReceivingQty: "Purchase · Receiving Quantity",
};
Object.keys(SALES_LINKED_COLUMN_MAP.QA).forEach((column) => {
  SALES_LINKED_LABELS[SALES_LINKED_COLUMN_MAP.QA[column]] = `QA · ${columnLabel(column)}`;
});
const PURCHASE_LINKED_SALES_COLUMNS = {
  InvoiceNumber: "LinkedSalesInvoiceNumber",
  InvoiceDate: "LinkedSalesInvoiceDate",
  PoNumber: "LinkedSalesPoNumber",
  PoDate: "LinkedSalesPoDate",
  ClientName: "LinkedSalesClientName",
  ConsigneeCountry: "LinkedSalesConsigneeCountry",
  ConsigneeName: "LinkedSalesConsigneeName",
  BrandName: "LinkedSalesBrandName",
  Grade: "LinkedSalesGrade",
  Variety: "LinkedSalesVariety",
  VarietyShortName: "LinkedSalesVarietyShortName",
  Species: "LinkedSalesSpecies",
  ProductType: "LinkedSalesProductType",
  Treatment: "LinkedSalesTreatment",
  PackingStyle: "LinkedSalesPackingStyle",
  MCS: "LinkedSalesMCS",
  LineGrossKGS: "LinkedSalesLineGrossKGS",
  InvoiceGrossKGS: "LinkedSalesInvoiceGrossKGS",
  PriceNative: "LinkedSalesPriceNative",
  Currency: "LinkedSalesCurrency",
  LineValueUSD: "LinkedSalesLineValueUSD",
  DestinationCountry: "LinkedSalesDestinationCountry",
  ContainerNo: "LinkedSalesContainerNo",
  ShipmentDate: "LinkedSalesShipmentDate",
  ShippingBillNo: "LinkedSalesShippingBillNo",
  ShippingBillDate: "LinkedSalesShippingBillDate",
  PoID: "LinkedSalesPoID",
  PoStatus: "LinkedSalesPoStatus",
  OrderedMCS: "LinkedSalesOrderedMCS",
  RevisedMCS: "LinkedSalesRevisedMCS",
  DispatchedMCS: "LinkedSalesDispatchedMCS",
  Price: "LinkedSalesPrice",
  CollectionRate: "LinkedSalesCollectionRate",
  DueDate: "LinkedSalesDueDate",
  ETAPOD: "LinkedSalesETAPOD",
  BLDate: "LinkedSalesBLDate",
  BankCharges: "LinkedSalesBankCharges",
  UtilizedValue: "LinkedSalesUtilizedValue",
  FirstCollectionDate: "LinkedSalesFirstCollectionDate",
  LastCollectionDate: "LinkedSalesLastCollectionDate",
  LastUtilizedDate: "LinkedSalesLastUtilizedDate",
  SpotRate: "LinkedSalesSpotRate",
  LinkedStockID: "LinkedSalesStockID",
  LinkedPoGradeID: "LinkedSalesPoGradeID",
  LinkedStockGrade: "LinkedSalesGrade",
  LinkedStockVarietyID: "LinkedSalesStockVarietyID",
  LinkedStockInvoiceNumbers: "LinkedSalesInvoiceNumber",
  LinkedStockActualMCS: "LinkedSalesStockActualMCS",
  LinkedStockAvailableMCS: "LinkedSalesStockAvailableMCS",
  LinkedReceivingID: "LinkedSalesReceivingID",
  LinkedTrackCode: "LinkedSalesTrackCode",
  LinkedArrivalID: "LinkedSalesArrivalID",
  LinkedArrivalBreakupID: "LinkedSalesArrivalBreakupID",
  LinkedProductionID: "LinkedSalesProductionID",
  LinkedProductionDate: "LinkedSalesProductionDate",
  LinkedProductionPlantID: "LinkedSalesProductionPlantID",
  LinkedReceivingQty: "LinkedSalesReceivingQty",
  LinkedPurchaseLotID: "LinkedSalesPurchaseLotID",
  LinkedPurchaseArrivalNumber: "LinkedSalesPurchaseArrivalNumber",
  LinkedPurchaseLotNumber: "LinkedSalesPurchaseLotNumber",
  LinkedPurchaseDate: "LinkedSalesPurchaseDate",
  LinkedSupplierName: "LinkedSalesSupplierName",
  LinkedPurchaseStation: "LinkedSalesPurchaseStation",
  LinkedPurchaseWeight: "LinkedSalesPurchaseWeight",
  LinkedStockOutID: "LinkedSalesStockOutID",
  LinkedStockOutBatchID: "LinkedSalesStockOutBatchID",
  LinkedDispatchDate: "LinkedSalesDispatchDate",
  LinkedDispatchType: "LinkedSalesDispatchType",
  LinkedDispatchMCS: "LinkedSalesDispatchMCS",
  LinkedDispatchLooseBags: "LinkedSalesDispatchLooseBags",
  LinkedQARowID: "LinkedQARowID",
  LinkedQALotID: "LinkedQALotID",
  LinkedQARMLotId: "LinkedQARMLotId",
  LinkedQASampleKey: "LinkedQASampleKey",
  LinkedQASampleDetails: "LinkedQASampleDetails",
  LinkedQAAnalysisDate: "LinkedQAAnalysisDate",
  LinkedQAReportDate: "LinkedQAReportDate",
  LinkedQALotNo: "LinkedQALotNo",
  LinkedQACentre: "LinkedQACentre",
  LinkedQAPlantID: "LinkedQAPlantID",
  LinkedQAPlant: "LinkedQAPlant",
  LinkedQAResult: "LinkedQAResult",
  LinkedQAParameter: "LinkedQAParameter",
  LinkedQASulphite: "LinkedQASulphite",
  LinkedQAAOZ: "LinkedQAAOZ",
  LinkedQASEM: "LinkedQASEM",
  LinkedQACAP: "LinkedQACAP",
  LinkedQAAHD: "LinkedQAAHD",
  LinkedQAAMOZ: "LinkedQAAMOZ",
  LinkedQARemarks: "LinkedQARemarks",
  LinkedQAExternalLab: "LinkedQAExternalLab",
  LinkedQAExternalResult: "LinkedQAExternalResult",
  LinkedQAAbStatus: "LinkedQAAbStatus",
  LinkedQATestedBy: "LinkedQATestedBy",
  LinkedQAAntibioticDetected: "LinkedQAAntibioticDetected",
  LinkedQATestClear: "LinkedQATestClear",
};

function readSavedDetailColumns() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(MS_DETAIL_COLUMNS_STORAGE_KEY) || "[]");
    const valid = Array.isArray(saved)
      ? saved.filter((id) => typeof id === "string" && VALID_MS_DETAIL_COLUMN_IDS.has(id))
      : [];
    return [...new Set(valid)];
  } catch {
    return [];
  }
}

const PAGE_SIZE = 10;
const isEmptyReportValue = (value) => value === null || value === undefined
  || (typeof value === "string" && value.trim() === "");
const cellText = (value, columnName) => (
  isEmptyReportValue(value) ? "-" : formatReportValue(value, columnName)
);

// Each selected source retains its own ERP rows. Sources that do not share a
// verified business key are displayed side by side without claiming a join.
function CombinedTable({ sources, filters }) {
  const [paging, setPaging] = useState({ key: "", page: 1 });
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const pageSize = PAGE_SIZE;
  const key = JSON.stringify([sources.map((s) => [s.id, s.columns]), filters, query, pageSize]);
  const page = paging.key === key ? paging.page : 1; // new columns/filters/search start at page 1
  const goTo = (p) => setPaging({ key, page: p });

  useEffect(() => {
    let live = true;
    setLoading(true);
    (async () => {
      const out = [];
      for (let index = 0; index < sources.length && live; index += 4) {
        out.push(...await Promise.all(sources.slice(index, index + 4).map((source) => api.post("/report/query", {
          widget: source.widget,
          filters: createCeoFilters(source.widget, filters),
          page, page_size: pageSize, search: query || null, selected_columns: source.columns,
        }, {
          // Purchase links Sales, receiving, and QA rows; let its server-side
          // query timeout govern instead of aborting at the generic 60s limit.
          timeout: source.widget.dataset_id === DS.msPurchasePreprocessing ? 0 : 60000,
        }).then(
          (r) => ({ s: source, data: r.data }),
          (e) => ({ s: source, error: e?.response?.data?.detail || e?.message || "Failed to load" }),
        ))));
      }
      if (live) { setResults(out); setLoading(false); }
    })();
    return () => { live = false; };
  }, [key, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const columnCount = sources.reduce((n, s) => n + s.columns.length, 0);
  const sections = results.map(({ s, data }) => ({
    s,
    total: data?.total_count ?? null,
    pages: data?.total_pages || 0,
    hasNext: Boolean(data?.has_next),
    rows: (data?.rows || []).map((row) => Object.fromEntries(
      (data?.columns || []).map((column, index) => [String(column).toLowerCase(), row[index]])
    )),
  }));
  const hasSourceError = results.some((result) => Boolean(result.error));
  // Separate sources have no shared row key. Never pad one failed source with
  // NULLs beside another source's records by aligning the pages by row number.
  const lineCount = hasSourceError ? 0 : sections.reduce((count, section) => Math.max(count, section.rows.length), 0);
  const totalKnown = results.length > 0 && results.every((result) => Number.isFinite(result.data?.total_count));
  const total = totalKnown ? results.reduce((count, result) => count + result.data.total_count, 0) : null;
  const pages = results.reduce((count, result) => Math.max(count, result.data?.total_pages || 0), 0);
  const hasNext = sections.some((section) => section.hasNext);
  const problems = results.flatMap(({ s, data, error }) => [
    // The API says "No valid report columns selected" when none of the ticked
    // columns exist in the live database (the picker lists declared fields).
    ...(error ? [`${s.title}: ${String(error).replace("No valid report columns selected", "none of the ticked columns exist in the live ERP database")}`] : []),
    ...(data?.unavailable_columns?.length ? [`${s.title}: not in the live ERP database: ${data.unavailable_columns.map(columnLabel).join(", ")}`] : []),
  ]);
  const pageBtn = "rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40";

  return (
    <div className="rounded-2xl border bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[15px] font-semibold text-slate-900">Detailed Report</div>
          <div className="text-xs text-slate-500">
            {columnCount} selected column(s) from {sources.length} report source(s) · {total === null ? `${sections.reduce((n, section) => n + section.rows.length, 0)} rows on this page${hasNext ? " · more available" : ""}` : `${total.toLocaleString("en-IN")} records`}{loading ? " · loading…" : ""}
          </div>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search selected fields…" className="w-64 max-w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
      </div>
      {query ? <div className="mt-2 text-xs text-blue-700">Search checks selected columns within the current report filters.</div> : null}
      {sources.some((source) => source.widget.dataset_id === DS.msSalesPoTrace) ? (
        <div className="mt-2 text-xs text-slate-500">Sales is anchored on PO and follows stock → production → receiving → purchase lot. QA joins by RMLotId. A NULL downstream value means no matching record exists on that link path yet, such as an arrival with no production or stock record.</div>
      ) : null}
      {sources.some((source) => source.widget.dataset_id === DS.msPurchasePreprocessing) ? (
        <div className="mt-2 text-xs text-slate-500">Purchase is the report anchor. Selected QA fields are merged by RMLotId (QC LotID = Purchase RmLotID); a purchase lot can appear on multiple rows when it has multiple QA tests. Purchase links to receiving by Arrival ID and Arrival Breakup ID.</div>
      ) : null}
      {problems.length ? (
        <div className="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          {problems.map((p) => <div key={p}>{p}</div>)}
        </div>
      ) : null}
      <div className={`mt-3 max-h-[70vh] overflow-auto rounded-xl border border-slate-200 ${loading ? "opacity-60" : ""}`}>
        <table className="report-data-table min-w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-slate-50 text-xs text-slate-500 shadow-[0_1px_0_#e2e8f0]">
            <tr>
              <th rowSpan={2} className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold">S.No.</th>
              {sections.map(({ s, total: count }) => (
                <th key={s.id} colSpan={s.columns.length} className="whitespace-nowrap border-b border-l border-slate-200 px-3 py-1.5 text-center font-semibold normal-case text-slate-700">
                  {s.module} · {s.title} <span className="font-normal text-slate-400">({count === null ? "page rows" : count.toLocaleString("en-IN")})</span>
                </th>
              ))}
            </tr>
            <tr className="uppercase tracking-wide">
              {sections.flatMap(({ s }) => s.columns.map((c, j) => (
                <th key={`${s.id}-${c}`} className={`whitespace-nowrap px-3 py-2 text-center font-semibold ${j === 0 ? "border-l border-slate-200" : ""}`}>{s.columnLabels?.[c] || columnLabel(c)}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {lineCount ? Array.from({ length: lineCount }, (_, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="whitespace-nowrap px-3 py-1.5 text-center text-slate-400">{(page - 1) * pageSize + i + 1}</td>
                {sections.flatMap(({ s, rows }) => s.columns.map((c, j) => (
                  <td key={`${s.id}-${c}`} className={`whitespace-nowrap px-3 py-1.5 text-center text-slate-900 ${j === 0 ? "border-l border-slate-100" : ""}`}>
                    {cellText(rows[i]?.[String(c).toLowerCase()], c)}
                  </td>
                )))}
              </tr>
            )) : (
              <tr><td colSpan={columnCount + 1} className="px-3 py-10 text-center text-slate-500">{loading ? "Loading…" : hasSourceError ? "Rows hidden because a report source failed. Successful sources are not aligned by row number." : "No records for these filters."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-sm text-slate-600">
        <span className="mr-auto text-xs text-slate-400">{pageSize} lines per page</span>
        <button type="button" disabled={loading || page <= 1} onClick={() => goTo(page - 1)} className={pageBtn}>Previous</button>
        <span>{pages ? `Page ${page} of ${pages}` : `Page ${page}${hasNext ? " · more available" : ""}`}</span>
        <button type="button" disabled={loading || (pages ? page >= pages : !hasNext)} onClick={() => goTo(page + 1)} className={pageBtn}>Next</button>
      </div>
    </div>
  );
}

function DetailedReport({ filters }) {
  const [picked, setPicked] = useState(readSavedDetailColumns);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [exportMode, setExportMode] = useState("");

  useEffect(() => {
    try {
      window.localStorage.setItem(MS_DETAIL_COLUMNS_STORAGE_KEY, JSON.stringify(picked));
    } catch {
      // Keep report selection usable if browser storage is unavailable.
    }
  }, [picked]);

  const matched = useMemo(() => {
    const on = new Set(picked);
    return SOURCES
      .map((s) => ({ ...s, columns: s.columns.filter((c) => on.has(pickId(s.module, c))) }))
      .filter((s) => s.columns.length);
  }, [picked]);
  const visibleMatched = useMemo(
    () => {
      // When Sales is selected alongside other modules, use its PO-keyed trace
      // automatically. Showing the same columns as independent tables and
      // lining pages up by row number creates NULLs that look like failed links.
      if (moduleFilter === "all" && !matched.some((source) => source.module === "Sales")) return matched;
      if (moduleFilter === "Purchase") {
        const purchaseSource = matched.find((source) => source.module === "Purchase");
        if (!purchaseSource) return [];
        const nativePurchaseSource = {
          ...purchaseSource,
          id: "ms-report-purchase-native",
          title: "Purchase Details",
          widget: { ...purchaseSource.widget, dataset_id: DS.rmPurchase },
        };
        const qaSource = matched.find((source) => source.module === "QA");
        const salesSource = matched.find((source) => source.module === "Sales");
        const qaColumns = [];
        const salesColumns = [];
        const columnLabels = {};
        (qaSource?.columns || []).forEach((column) => {
          const alias = SALES_LINKED_COLUMN_MAP.QA[column];
          if (alias && !qaColumns.includes(alias)) {
            qaColumns.push(alias);
            columnLabels[alias] = SALES_LINKED_LABELS[alias] || `QA · ${columnLabel(column)}`;
          }
        });
        (salesSource?.columns || []).forEach((column) => {
          const alias = PURCHASE_LINKED_SALES_COLUMNS[column];
          if (alias && !salesColumns.includes(alias)) {
            salesColumns.push(alias);
            columnLabels[alias] = `Sales · ${columnLabel(column)}`;
          }
        });
        if (!salesColumns.length && !qaColumns.length) return [nativePurchaseSource];
        return [{
          ...purchaseSource,
          id: "ms-report-purchase-qa-linked",
          title: "Purchase — Sales and QA Linked Details",
          columns: [...new Set([...purchaseSource.columns, ...salesColumns, ...qaColumns])],
          columnLabels,
        }];
      }
      if (moduleFilter === "Process") {
        return matched.filter((source) => source.module === "Process" || source.module === "Pre-processing");
      }
      if (moduleFilter !== "Sales" && moduleFilter !== "all") return matched.filter((source) => source.module === moduleFilter);

      const salesSource = matched.find((source) => source.module === "Sales");
      if (!salesSource) return [];
      const linkedColumns = [];
      const linkedColumnLabels = {};
      matched.filter((source) => source.module !== "Sales").forEach((source) => {
        const moduleMap = SALES_LINKED_COLUMN_MAP[source.module === "Pre-processing" ? "Process" : source.module] || {};
        source.columns.forEach((column) => {
          const alias = moduleMap[column];
          if (alias && !linkedColumns.includes(alias)) {
            linkedColumns.push(alias);
            linkedColumnLabels[alias] = SALES_LINKED_LABELS[alias] || `${source.module} · ${columnLabel(column)}`;
          }
        });
      });
      if (!linkedColumns.length) {
        return [{
          ...salesSource,
          id: "ms-report-sales-native",
          title: "Sales Details",
          widget: { ...salesSource.widget, dataset_id: DS.msSales },
        }];
      }
      const primarySalesColumns = salesSource.columns.map((column) => ({
        PoID: "PrimaryPoID",
        PoNumber: "PrimaryPoNumber",
        PoDate: "PrimaryPoDate",
      }[column] || column));
      const columnLabels = {
        ...Object.fromEntries(primarySalesColumns
          .filter((column) => SALES_LINKED_LABELS[column])
          .map((column) => [column, SALES_LINKED_LABELS[column]])),
        ...linkedColumnLabels,
      };
      const columns = [...primarySalesColumns, ...linkedColumns];
      return [{
        ...salesSource,
        id: "ms-report-sales-po-primary",
        title: "Sales — PO-Linked Cross-Module Details",
        columns,
        columnLabels,
      }];
    },
    [matched, moduleFilter],
  );
  const changeModule = (module) => {
    setModuleFilter(module);
    if (module === "all") return;
    const moduleDefaults = DEFAULT_DETAIL_COLUMN_IDS_BY_MODULE[module] || [];
    setPicked([...new Set(moduleDefaults)]);
  };

  const exportAll = async (consolidated = false) => {
    setExporting(true);
    setExportMode(consolidated ? "consolidated" : "workbook");
    try {
      const sheets = visibleMatched.map((s) => ({
        title: s.title,
        widget: s.widget,
        filters: createCeoFilters(s.widget, filters),
        selected_columns: s.columns,
        column_labels: Object.fromEntries(s.columns.map((column) => [column, s.columnLabels?.[column] || columnLabel(column)])),
        plain_headers: s.module === "Sales",
      }));
      // Large exports take minutes, so the server builds the file in the background.
      const endpoint = consolidated ? "/ms/detail-export/consolidated/start" : "/ms/detail-export/start";
      const { data: job } = await api.post(endpoint, { sheets });
      for (;;) {
        await new Promise((res) => setTimeout(res, 3000));
        const { data: st } = await api.get(`/ms/detail-export/${job.job_id}`);
        if (st.status === "error") throw new Error(st.error || "Export failed.");
        if (st.status === "done") break;
      }
      const r = await api.get(`/ms/detail-export/${job.job_id}/file`, { responseType: "blob", timeout: 300000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      a.download = `ms_${consolidated ? "consolidated_" : "detailed_"}report_${filters.dateFrom}_${filters.dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Unable to export.");
    } finally {
      setExporting(false);
      setExportMode("");
    }
  };

  return (
    <>
      <ColumnPicker
        title="Detailed report columns"
        hint="the detail tables shown on the CEO Command Center tabs"
        groups={COLUMN_GROUPS}
        selected={picked}
        onChange={setPicked}
        moduleOptions={["Sales", "Purchase", "Process", "Store"]}
        moduleValue={moduleFilter}
        onModuleChange={changeModule}
      >
              {visibleMatched.length ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <span className="text-xs text-slate-500">
              Consolidated report from {visibleMatched.length} selected table(s): {visibleMatched.map((s) => s.title).join(", ")}.
            </span>
            <div className="flex flex-wrap gap-2">
              {/* Multi-sheet workbook download is intentionally disabled.
              <button type="button" disabled={exporting} onClick={() => exportAll(false)} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60">
                {exporting && exportMode === "workbook" ? "Preparing workbook…" : `Download Excel (all ${matched.length} sheets)`}
              </button>
              */}
              <button type="button" disabled={exporting} onClick={() => exportAll(true)} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity60">
                {exporting && exportMode === "consolidated" ? "Preparing report…" : "Download Excel Report"}
              </button>
            </div>
          </div>
        ) : null}
      </ColumnPicker>

      {visibleMatched.length ? <CombinedTable sources={visibleMatched} filters={filters} /> : null}
    </>
  );
}

export default function MsReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [options, setOptions] = useState({});
  const [selected, setSelected] = useState(DEFAULT_KPIS);
  const [values, setValues] = useState({}); // `${id}|${filterKey}` -> number | { error }

  const applyDatePreset = (preset) => {
    const now = new Date();
    const year = now.getFullYear();
    if (preset === "clear") {
      setFilters((current) => ({ ...current, dateFrom: "", dateTo: "" }));
      return;
    }

    let fromYear = year;
    let fromMonth = "01";
    let fromDay = "01";
    let toYear = year;
    let toMonth = "12";
    let toDay = "31";
    if (preset === "fy") {
      fromYear = now.getMonth() >= 3 ? year : year - 1;
      toYear = fromYear + 1;
      fromMonth = "04";
      fromDay = "01";
      toMonth = "03";
      toDay = "31";
    } else if (preset === "previous") {
      fromYear = year - 1;
      toYear = fromYear;
    }

    setFilters((current) => ({
      ...current,
      dateFrom: `${fromYear}-${fromMonth}-${fromDay}`,
      dateTo: `${toYear}-${toMonth}-${toDay}`,
    }));
  };

  useEffect(() => {
    api.get("/ceo/filter-options").then((r) => {
      setCeoPlantAliases(r.data?.plantAliases);
      setOptions(r.data || {});
    }).catch(() => {});
  }, []);

  const filterKey = JSON.stringify(filters);
  const columns = useMemo(() => KPIS.filter((k) => selected.includes(k.id)), [selected]);
  const cell = (k) => values[`${k.id}|${filterKey}`];

  useEffect(() => {
    const queue = columns.filter((k) => cell(k) === undefined);
    if (!queue.length) return undefined;
    let cancelled = false;
    const f = JSON.parse(filterKey);
    // ponytail: 4 requests at a time keeps the live ERP load close to one CEO tab.
    const worker = async () => {
      while (queue.length && !cancelled) {
        const k = queue.shift();
        let v;
        try { v = await fetchKpi(k, f); } catch (e) { v = { error: e?.response?.data?.detail || e?.message || "Failed" }; }
        if (!cancelled) setValues((cur) => ({ ...cur, [`${k.id}|${filterKey}`]: v }));
      }
    };
    [1, 2, 3, 4].forEach(() => worker());
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, filterKey]);

  const pending = columns.filter((k) => cell(k) === undefined).length;
  const ready = columns.length > 0 && pending === 0;

  const filterCols = [["Period From", formatDateValue(filters.dateFrom)], ["Period To", formatDateValue(filters.dateTo)], ...FILTERS.map(([key, label]) => [label, filters[key] || "All"])];
  const exportHeader = ["S.No", ...filterCols.map(([l]) => l), ...columns.map((k) => k.title)];
  const exportRow = [1, ...filterCols.map(([, v]) => v), ...columns.map((k) => (typeof cell(k) === "number" ? cell(k) : ""))];

  const download = (blob, ext) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `ms_report_${filters.dateFrom}_${filters.dateTo}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const text = [exportHeader, exportRow].map((r) => r.map(esc).join(",")).join("\r\n");
    download(new Blob([`﻿${text}`], { type: "text/csv;charset=utf-8" }), "csv");
  };
  const exportExcel = async () => {
    try {
      const r = await api.post("/ms/export.xlsx", { columns: exportHeader, rows: [exportRow] }, { responseType: "blob" });
      download(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "xlsx");
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Unable to export Excel.");
    }
  };

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />
      <main className="erp-content space-y-4 p-4">
        <div className="ceo-filter-panel">
          <div className="ceo-filter-head">
            <div>
              <div className="ceo-filter-title">MS Report Filters</div>
              <div className="ceo-filter-subtitle">
                KPI summary and detailed ERP report filters. KPI values match the CEO cards for the same filters.
              </div>
            </div>
            <div className="ceo-filter-actions" aria-label="Date range shortcuts">
              <button type="button" onClick={() => applyDatePreset("fy")}>Current FY</button>
              <button type="button" onClick={() => applyDatePreset("cy")}>Current CY</button>
              <button type="button" onClick={() => applyDatePreset("previous")}>Previous Year</button>
              <button type="button" onClick={() => applyDatePreset("clear")}>Clear</button>
            </div>
          </div>
          <div className="ceo-filter-grid">
            <div>
              <label>Date From</label>
              <DateInput value={filters.dateFrom} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
            </div>
            <div>
              <label>Date To</label>
              <DateInput value={filters.dateTo} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))} />
            </div>
            {FILTERS.map(([key, label, optKey]) => (
              <div key={key} className={key === "consignee" ? "ms-consignee-filter" : ""}>
                <label>{label}</label>
                {true ? (
                  <MsVarietyFilter
                    value={filters[key]}
                    options={key === "productType"
                      ? (options[optKey] || []).map((value) => ({
                          value,
                          label: ({ RAW: "Raw", COOKED: "Cooked", BLANCHED: "Blanched" })[String(value).toUpperCase()] || value,
                        }))
                      : options[optKey] || []}
                    onChange={(nextValue) =>
                      setFilters((f) => ({ ...f, [key]: nextValue }))
                    }
                    filterKey={key}
                    filterLabel={label}
                  />
                ) : (
                  <select value={filters[key]} onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))} className="max-w-[200px] rounded-xl border border-slate-300 px-3 py-2">
                    <option value="">All</option>
                    {(options[optKey] || []).map((o) => <option key={optValue(o)} value={optValue(o)}>{optLabel(o)}</option>)}
                  </select>
                )}
              </div>
            ))}
          </div>
          <div className="ceo-filter-footer">
            <button type="button" onClick={() => setFilters(defaultFilters())} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              Reset Filters
            </button>
            <span>Filters apply where supported by each KPI, matching CEO Command Center behavior.</span>
          </div>
        </div>

        {/* Temporarily hidden: KPI summary column selector
        <ColumnPicker
          title="KPI summary columns"
          hint="grouped by CEO Command Center tab"
          groups={KPI_GROUPS}
          selected={selected}
          onChange={setSelected}
        />

        */}

        {/* Temporarily hidden: KPI Summary card, table, and downloads
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-[15px] font-semibold text-slate-900">KPI Summary — {formatDateValue(filters.dateFrom)} to {formatDateValue(filters.dateTo)}</div>
              <div className="text-xs text-slate-500">{columns.length} KPI column(s){pending ? ` · loading ${pending}…` : ""}</div>
            </div>
            <div className="flex gap-2">
              <button type="button" disabled={!ready} onClick={exportCsv} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Download CSV</button>
              <button type="button" disabled={!ready} onClick={exportExcel} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">Download Excel</button>
            </div>
          </div>
          {columns.length ? (
            <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    {columns.map((k) => (
                      <th key={k.id} className="whitespace-nowrap px-3 py-2 text-right">
                        <div className="text-[10px] font-normal normal-case text-slate-400">{k.module}</div>
                        {k.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {columns.map((k) => {
                      const v = cell(k);
                      return (
                        <td key={k.id} className="whitespace-nowrap px-3 py-3 text-right font-semibold text-slate-900" title={v?.error || ""}>
                          {v === undefined ? <span className="text-slate-400">…</span> : typeof v === "number" ? fmt(k, v) : <span className="text-xs font-normal text-rose-600">Not available</span>}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">Tick at least one KPI above.</div>
          )}
        </div>

        */}

        <DetailedReport filters={filters} />
      </main>
    </div>
  );
}
